import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BarChart3, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CoverImage } from "@/components/CoverImage";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MetricCard, DspBars, DailyChart, Change, fmtNum, SOCIAL_COLORS, SOCIAL_LABEL } from "@/components/analytics/Widgets";
import { t, fmtDate, fmtDateTime } from "@/i18n";

export default function AnalyticsPage() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/analytics/overview").then((r) => setData(r.data)).catch(() => setData({ releases: [], totals: {}, by_dsp: [], daily: [] }));
  useEffect(() => { load(); }, []);

  const syncAll = async () => {
    setBusy(true);
    try { await api.post("/analytics/sync-all"); toast.success(t("Actualización iniciada. Los datos aparecerán en unos minutos.")); setTimeout(load, 20000); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!data) return <div className="panel p-10 text-center text-sm text-muted-foreground">{t("Cargando…")}</div>;
  const rows = data.releases.filter((r) => `${r.title} ${r.artist_name}`.toLowerCase().includes(q.toLowerCase()));
  const sumDaily = (n) => (data.daily || []).slice(-n).reduce((a, d) => a + (d.streams || 0), 0);
  const streams7 = data.totals.streams_7d || sumDaily(7);
  const streams30 = data.totals.streams_30d || sumDaily(30);
  const range = data.range_7d ? `${fmtDate(data.range_7d.from + "T12:00:00", { day: "numeric", month: "short" })} – ${fmtDate(data.range_7d.to + "T12:00:00", { day: "numeric", month: "short" })}` : "";
  const admin = can("admin");

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-primary" />{t("Promoción")}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">{t("Analíticas")}</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl" data-testid="analytics-subtitle">
            {range ? t("Streams, vistas en redes, playlists y audiencia de tus lanzamientos · últimos 7 días ({range}) · se actualiza a diario a las 12:00 pm", { range }) : t("Streams, vistas en redes, playlists y audiencia de tus lanzamientos · se actualiza a diario a las 12:00 pm")}
            {admin && data.last_sync_at ? ` · ${t("Actualizado")} ${fmtDateTime(data.last_sync_at)}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("Buscar…")} className="w-full sm:w-64" data-testid="analytics-search-input" />
          {admin && <Button variant="outline" onClick={syncAll} disabled={busy} className="gap-2 shrink-0" data-testid="analytics-sync-all"><RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} /> {t("Actualizar todo")}</Button>}
        </div>
      </div>

      {data.releases.length === 0 ? (
        <div className="panel p-10 text-center text-sm text-muted-foreground" data-testid="analytics-empty">
          <BarChart3 className="h-6 w-6 mx-auto mb-3 opacity-50" />{t("Todavía no hay analíticas. Aparecen automáticamente cuando un lanzamiento está en distribución y recibe streams.")}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3" data-testid="analytics-totals">
            <MetricCard label="Streams · 7 días" metric={{ value: streams7 }} testId="total-streams-7d" />
            <MetricCard label="Streams · 30 días" metric={{ value: streams30 }} testId="total-streams-30d" />
            <MetricCard label="Oyentes · 7 días" metric={{ value: data.totals.listeners_7d, display: fmtNum(data.totals.listeners_7d) }} />
            <MetricCard label="Guardados · 7 días" metric={{ value: data.totals.saves_7d, display: fmtNum(data.totals.saves_7d) }} />
            <MetricCard label="Vistas en redes · 7 días" hint={t("YouTube, TikTok, Facebook, Instagram")} metric={{ value: data.totals.social_views_7d, display: fmtNum(data.totals.social_views_7d) }} testId="total-social-views" />
            <MetricCard label="Videos creados · 7 días" hint={t("TikTok, Reels, Facebook")} metric={{ value: data.totals.social_creations_7d, display: fmtNum(data.totals.social_creations_7d) }} testId="total-social-creations" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-8 panel p-5 space-y-2">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("Streams por día · últimos 30 días")}</p>
              <DailyChart data={data.daily} series={[{ key: "streams", color: "hsl(var(--primary))" }]} testId="analytics-chart" />
            </div>
            <div className="lg:col-span-4 panel p-5 space-y-4">
              <div className="space-y-2"><p className="text-xs text-muted-foreground uppercase tracking-wider">{t("Streams por plataforma · 7 días")}</p><DspBars items={data.by_dsp} testId="analytics-dsp" /></div>
              <div className="space-y-2"><p className="text-xs text-muted-foreground uppercase tracking-wider">{t("Vistas en redes · 7 días")}</p><DspBars items={data.social_by_platform || []} field="views_7d" labelKey="platform" colors={SOCIAL_COLORS} labels={SOCIAL_LABEL} testId="analytics-social" /></div>
            </div>
          </div>
          <div className="panel overflow-hidden">
            <Table data-testid="analytics-table">
              <TableHeader><TableRow><TableHead>{t("Lanzamiento")}</TableHead><TableHead className="text-right">{t("Streams · 7 días")}</TableHead><TableHead className="text-right">{t("Semana")}</TableHead><TableHead className="text-right">{t("Streams · 30 días")}</TableHead><TableHead className="text-right">{t("Oyentes")}</TableHead><TableHead className="text-right">{t("Guardados")}</TableHead><TableHead className="text-right">{t("Vistas en redes")}</TableHead><TableHead>{t("Top país")}</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} className="group" data-testid={`analytics-row-${r.id}`}>
                    <TableCell>
                      <Link to={`/lanzamientos/${r.id}`} className="flex items-center gap-3 hover:text-primary transition-colors">
                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-md"><CoverImage fileId={r.cover_file_id} className="h-10 w-10 rounded-md transition-transform duration-500 group-hover:scale-110" /></div>
                        <div className="min-w-0"><p className="font-medium truncate">{r.title}</p><p className="text-xs text-muted-foreground truncate">{r.artist_name}</p></div>
                      </Link>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{r.streams_7d?.display ?? "—"}</TableCell>
                    <TableCell className="text-right"><Change pct={r.streams_7d?.change_pct} /></TableCell>
                    <TableCell className="text-right tabular-nums">{r.streams_30d?.display ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.listeners_7d?.display ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.saves_7d?.display ?? "—"}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtNum(r.social_views_7d)}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.top_territory || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
